/**
 * Confirmation gates for the AI-draft actions on an EXISTING (unlocked)
 * Progress Note. Both actions REPLACE the note's current content, so each
 * asks first — regenerate unconditionally (approved decision: no regenerate
 * ever runs without an explicit confirm), load whenever typed content is at
 * stake. These are the "are you sure" gates; the PD-1110 AI disclaimer
 * (`confirmAiNote`) still runs AFTER, before content lands in the form.
 */
import { Text } from '@mantine/core'
import { modals } from '@mantine/modals'

function confirmAction(opts: {
  title: string
  body: string
  confirmLabel: string
}): Promise<boolean> {
  return new Promise((resolve) => {
    modals.openConfirmModal({
      title: (
        <Text fw={700} size="md">
          {opts.title}
        </Text>
      ),
      centered: true,
      radius: 'md',
      children: (
        <Text size="sm" c="dimmed" lh={1.55}>
          {opts.body}
        </Text>
      ),
      labels: { confirm: opts.confirmLabel, cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
      // Mantine's modals reducer invokes onClose on every close — Cancel,
      // Escape, backdrop, "×", programmatic — and again after onConfirm/onCancel,
      // where the promise is already settled and the second resolve is a no-op.
      onClose: () => resolve(false),
    })
  })
}

/** Always shown before a regenerate — the AI will produce a DIFFERENT note. */
export function confirmRegenerateNote(): Promise<boolean> {
  return confirmAction({
    title: 'Regenerate this note?',
    body:
      'The AI will run again on the saved visit transcript and produce a new version of the note. ' +
      "The note's current content will be replaced. The transcript itself is never modified.",
    confirmLabel: 'Regenerate note',
  })
}

/** Shown before reloading the original AI-generated note over existing content. */
export function confirmLoadAiDraft(): Promise<boolean> {
  return confirmAction({
    title: 'Reload the original Progress Note?',
    body:
      "The Progress Note the AI originally generated for this visit will replace the note's " +
      'current content. Chart carry-forward stays; anything typed into the note may be overwritten.',
    confirmLabel: 'Reload original',
  })
}
